import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { api, setSessionExpiredHandler, setAccessTokenRefreshCallback, AUTH_REFRESH_TOKEN_KEY, isSessionExpiredError } from '@/services/api'
import { useAppStore } from '@/stores/app'
import { mediaService } from '@/services/MediaService'
import { companiesService } from '@/services/CompaniesService'
import {
  applyCompanyBrandingFromApi,
  getBrandingBusinessPatch
} from '@/utils/customization'

const pickAvatarFromProfileUpdate = (profileData, cleanApiData, existingUser) => {
  if (Object.prototype.hasOwnProperty.call(profileData, 'avatar')) {
    return profileData.avatar
  }
  return cleanApiData.avatar ?? existingUser?.avatar
}

export const isMediaUuid = (value) => {
  if (!value) return false
  if (value.startsWith('http') || value.startsWith('data:')) return false
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
}

const isHttpUrl = (value) => {
  return typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))
}

function extractOAuthPictureFromApi(apiUser) {
  if (!apiUser || typeof apiUser !== 'object') return null
  const keys = ['picture', 'oauth_picture', 'google_picture', 'oauth_picture_url']
  for (const k of keys) {
    const v = apiUser[k]
    if (v && isHttpUrl(String(v))) return String(v)
  }
  return null
}

function avatarFromApi(apiUser) {
  if (!apiUser || typeof apiUser !== 'object') return null
  const rawUrl = apiUser.avatar_url
  const rawAvatar = apiUser.avatar
  if (rawUrl != null && isMediaUuid(String(rawUrl))) return String(rawUrl)
  if (rawAvatar != null && isMediaUuid(String(rawAvatar))) return String(rawAvatar)
  const oauthFromFields = extractOAuthPictureFromApi(apiUser)
  const oauthFromAvatar = rawAvatar != null && isHttpUrl(String(rawAvatar)) ? String(rawAvatar) : null
  return oauthFromFields || oauthFromAvatar || null
}

function migrateUserFromLocalStorage(parsed) {
  if (!parsed || typeof parsed !== 'object') return parsed
  const next = { ...parsed }
  if (next.oauthAvatarUrl && !next.avatar) {
    next.avatar = next.oauthAvatarUrl
  }
  delete next.oauthAvatarUrl
  return next
}

function mergeBusinessWithCompanyFromAuth(responseData, loggedBusiness) {
  if (!responseData || typeof responseData !== 'object') return loggedBusiness
  const companies = responseData.companies || []
  let source = null
  if (Array.isArray(companies) && companies.length > 0) {
    companiesService.saveCompaniesToLocal(companies)
    source = companies[0]
    companiesService.setActiveCompany(companies[0])
  }
  if (!source && responseData.business) {
    source = responseData.business
  }
  if (source) {
    applyCompanyBrandingFromApi(source)
    return { ...loggedBusiness, ...getBrandingBusinessPatch(source) }
  }
  return loggedBusiness
}

function resolveAvatarAfterUpdate(profileData, cleanApiData, responseData, isPlainObject, existingUser) {
  const picked = pickAvatarFromProfileUpdate(profileData, cleanApiData, existingUser)

  if (Object.prototype.hasOwnProperty.call(profileData, 'avatar') && (profileData.avatar === '' || profileData.avatar === null)) {
    return null
  }

  if (picked !== undefined && picked !== null && picked !== '') {
    if (isMediaUuid(String(picked))) return String(picked)
    if (isHttpUrl(String(picked))) return String(picked)
  }

  if (isPlainObject && responseData) {
    const fromApi = avatarFromApi(responseData)
    if (fromApi) return fromApi
  }

  return existingUser?.avatar ?? null
}

function readImpersonationMeta() {
  try {
    return JSON.parse(localStorage.getItem('impersonation_meta') || 'null')
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const business = ref(null)
  const isLoading = ref(false)
  const error = ref(null)
  const token = ref(localStorage.getItem('auth_token') || null)
  const showSessionExpiredModal = ref(false)
  const isLoggingOut = ref(false)

  // Modo suplantación de solo lectura (handoff desde el panel admin).
  const impersonation = ref(readImpersonationMeta())
  const isImpersonating = computed(() => !!impersonation.value)

  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const isBusinessOwner = computed(() => user.value?.role === 'owner')
  const isEmployee = computed(() => user.value?.role === 'employee')
  const userName = computed(() => user.value?.name || '')
  const userEmail = computed(() => user.value?.email || '')

  const avatarUrl = ref(null)
  const companyLogoUrl = ref(null)
  const companyBannerUrl = ref(null)

  async function applyAvatarFromUserValue(avatar) {
    if (!avatar) {
      avatarUrl.value = null
      return
    }
    if (isMediaUuid(String(avatar))) {
      try {
        const url = await mediaService.getDownloadUrl(avatar)
        avatarUrl.value = url
      } catch (err) {
        console.error('Error resolviendo avatar UUID:', avatar, err)
        avatarUrl.value = null
      }
      return
    }
    if (isHttpUrl(String(avatar))) {
      avatarUrl.value = avatar
      return
    }
    avatarUrl.value = null
  }

  watch(
    () => user.value?.avatar,
    async (avatar) => {
      await applyAvatarFromUserValue(avatar)
    },
    { immediate: true }
  )

  async function syncAvatarUrlFromUser() {
    await applyAvatarFromUserValue(user.value?.avatar)
  }

  function getBusinessAssetValue(data, keys) {
    if (!data || typeof data !== 'object') return null
    for (const key of keys) {
      const value = data[key]
      if (value !== null && value !== undefined && value !== '') return value
    }
    return null
  }

  async function applyBusinessAssetFromValue(assetValue, targetRef, label) {
    if (!assetValue) {
      targetRef.value = null
      return
    }
    if (isMediaUuid(String(assetValue))) {
      try {
        targetRef.value = await mediaService.getDownloadUrl(String(assetValue))
      } catch (err) {
        console.error(`Error resolviendo ${label} UUID:`, assetValue, err)
        targetRef.value = null
      }
      return
    }
    if (isHttpUrl(String(assetValue))) {
      targetRef.value = String(assetValue)
      return
    }
    targetRef.value = null
  }

  async function syncBusinessAssetUrlsFromBusiness() {
    const logoValue = getBusinessAssetValue(business.value, ['logoCompany', 'logo_company', 'logo_url', 'logo'])
    const bannerValue = getBusinessAssetValue(business.value, ['bannerCompany', 'banner_company', 'banner'])
    await Promise.all([
      applyBusinessAssetFromValue(logoValue, companyLogoUrl, 'logoCompany'),
      applyBusinessAssetFromValue(bannerValue, companyBannerUrl, 'bannerCompany')
    ])
  }

  watch(
    () => [
      business.value?.logoCompany,
      business.value?.logo_company,
      business.value?.logo_url,
      business.value?.logo,
      business.value?.bannerCompany,
      business.value?.banner_company,
      business.value?.banner
    ],
    async () => {
      await syncBusinessAssetUrlsFromBusiness()
    },
    { immediate: true }
  )

  function persistRefreshTokenFromAuthResponse(responseData) {
    const nested = responseData?.data && typeof responseData.data === 'object' ? responseData.data : null
    const refresh =
      nested?.refresh_token ??
      nested?.refreshToken ??
      responseData?.refresh_token ??
      responseData?.refreshToken
    if (refresh) {
      localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refresh)
    } else {
      localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
    }
  }

  function normalizeApiUser(apiUser, fallbackEmail = '') {
    const roleName = typeof apiUser.role === 'object' && apiUser.role?.name
      ? apiUser.role.name
      : (apiUser.role || 'owner')
    return {
      ...apiUser,
      name: apiUser.name || apiUser.username || fallbackEmail.split('@')[0],
      role: roleName,
      avatar: avatarFromApi(apiUser)
    }
  }

  async function login(credentials) {
    isLoading.value = true
    error.value = null

    try {
      if (!credentials.email || !credentials.password) {
        throw new Error('Email y contraseña son obligatorios')
      }

      const response = await api.post('/auth/login', {
        email: credentials.email,
        password: credentials.password
      })

      const responseData = response.data || response
      const apiUser = responseData.user || response.user

      const loggedUser = apiUser ? normalizeApiUser(apiUser, credentials.email) : {
        id: responseData.id || response.id || Date.now(),
        email: credentials.email,
        name: credentials.email.split('@')[0],
        role: 'owner',
        createdAt: responseData.createdAt || new Date().toISOString(),
        lastLogin: new Date().toISOString()
      }

      const existingUser = (() => {
        try { return JSON.parse(localStorage.getItem('user_data') || 'null') } catch { return null }
      })()

      // Si es un usuario diferente, limpiar contexto de empresa para evitar
      // que el tenant-id de una sesión anterior contamine los requests del nuevo usuario
      if (existingUser?.email && existingUser.email !== loggedUser.email) {
        localStorage.removeItem('active_company')
        localStorage.removeItem('user_companies')
        localStorage.removeItem('business_data')
      }

      const cleanLoggedUser = Object.fromEntries(
        Object.entries(loggedUser).filter(([, v]) => v !== null && v !== undefined && v !== '')
      )

      const mergedUser = existingUser?.email === loggedUser.email
        ? {
            ...existingUser,
            ...cleanLoggedUser,
            avatar: loggedUser.avatar !== undefined ? loggedUser.avatar : existingUser.avatar
          }
        : loggedUser

      const loggedBusiness = responseData.business || response.business || {
        id: Date.now(),
        name: responseData.businessName || response.businessName || 'Mi Negocio',
        type: responseData.businessType || response.businessType || '',
        email: credentials.email
      }

      const mergedBusiness = mergeBusinessWithCompanyFromAuth(responseData, loggedBusiness)
      const authToken = responseData.token || response.token || response.access_token || ''

      user.value = mergedUser
      business.value = mergedBusiness
      token.value = authToken

      if (authToken) localStorage.setItem('auth_token', authToken)
      persistRefreshTokenFromAuthResponse(responseData)
      localStorage.setItem('user_data', JSON.stringify(mergedUser))
      _persistBusiness(mergedBusiness)

      try {
        await getProfile()
      } catch {
      }

      isLoggingOut.value = false
      return { user: user.value, business: mergedBusiness, token: authToken }
    } catch (err) {
      error.value = err.message || 'Error al iniciar sesión'
      console.error('Error en login:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function register(registrationData) {
    isLoading.value = true
    error.value = null

    try {
      if (!registrationData.email || !registrationData.password) {
        throw new Error('Email y contraseña son obligatorios')
      }
      if (registrationData.confirmPassword && registrationData.password !== registrationData.confirmPassword) {
        throw new Error('Las contraseñas no coinciden')
      }
      if (registrationData.password.length < 6) {
        throw new Error('La contraseña debe tener al menos 6 caracteres')
      }

      const response = await api.post('/auth/register', {
        email: registrationData.email,
        password: registrationData.password,
        username: registrationData.ownerName || registrationData.email.split('@')[0]
      })

      const responseData = response.data || response
      const apiUser = responseData.user || response.user

      const newUser = apiUser ? normalizeApiUser(apiUser, registrationData.email) : {
        id: responseData.id || response.id || Date.now(),
        email: registrationData.email,
        name: registrationData.ownerName || registrationData.businessName || registrationData.email.split('@')[0],
        role: 'owner',
        createdAt: new Date().toISOString()
      }

      const newBusiness = responseData.business || response.business || {
        id: Date.now(),
        name: registrationData.businessName || '',
        type: registrationData.businessType || '',
        email: registrationData.email
      }

      const mergedRegisterBusiness = mergeBusinessWithCompanyFromAuth(responseData, newBusiness)
      const newToken = responseData.token || response.token || response.access_token || ''

      user.value = newUser
      business.value = mergedRegisterBusiness
      token.value = newToken

      if (newToken) localStorage.setItem('auth_token', newToken)
      persistRefreshTokenFromAuthResponse(responseData)
      localStorage.setItem('user_data', JSON.stringify(newUser))
      _persistBusiness(mergedRegisterBusiness)

      isLoggingOut.value = false
      return { user: newUser, business: mergedRegisterBusiness, token: newToken }
    } catch (err) {
      error.value = err.message || 'Error al registrar usuario'
      console.error('Error en registro:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function clearSessionStorage() {
    localStorage.removeItem('auth_token')
    localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
    localStorage.removeItem('user_data')
    localStorage.removeItem('business_data')
    localStorage.removeItem('active_company')
    localStorage.removeItem('user_companies')
    localStorage.removeItem('impersonation_meta')
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith('cfm_')) keysToRemove.push(k)
    }
    keysToRemove.forEach(k => localStorage.removeItem(k))
  }

  async function logout() {
    isLoggingOut.value = true
    showSessionExpiredModal.value = false
    isLoading.value = true

    // Revocación server-side best-effort — el logout local (abajo) es lo que
    // realmente importa y tiene que funcionar siempre, esto es solo un plus
    // para que la fila de refresh token quede revocada en DB también.
    const refreshTokenToRevoke = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY)
    if (refreshTokenToRevoke) {
      api.post('/auth/logout', { refreshToken: refreshTokenToRevoke }, false).catch(() => {})
    }

    try {
      user.value = null
      business.value = null
      token.value = null
      error.value = null
      impersonation.value = null
      clearSessionStorage()
    } catch (err) {
      console.error('Error en logout:', err)
      user.value = null
      business.value = null
      token.value = null
      clearSessionStorage()
    } finally {
      isLoading.value = false
    }
  }

  function setFromOAuthCallback(payload) {
    const { token: authToken, userData, refreshToken, refresh_token } = payload
    if (!authToken || !userData) return

    const rawUser = userData.user || userData
    const loggedUser = normalizeApiUser(rawUser, rawUser.email || '')
    const companies = userData.companies || []
    const businessPayload = userData.business || (companies.length ? companies[0] : null)
    let nextBusiness = businessPayload || {
      id: Date.now(),
      name: 'Mi Negocio',
      type: '',
      email: loggedUser.email
    }

    if (companies.length > 0) {
      localStorage.setItem('user_companies', JSON.stringify(companies))
      const active = companies[0]
      if (active) {
        localStorage.setItem('active_company', JSON.stringify(active))
        applyCompanyBrandingFromApi(active)
        nextBusiness = { ...nextBusiness, ...getBrandingBusinessPatch(active) }
      }
    } else if (businessPayload) {
      applyCompanyBrandingFromApi(businessPayload)
      nextBusiness = { ...nextBusiness, ...getBrandingBusinessPatch(businessPayload) }
    }

    user.value = loggedUser
    business.value = nextBusiness
    token.value = authToken
    isLoggingOut.value = false

    localStorage.setItem('auth_token', authToken)
    const rt = refreshToken || refresh_token || userData.refresh_token || userData.refreshToken
    if (rt) {
      localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, rt)
    } else {
      localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
    }
    localStorage.setItem('user_data', JSON.stringify(loggedUser))
    _persistBusiness(nextBusiness)
  }

  async function initializeAuth() {
    setSessionExpiredHandler(handleSessionExpired)
    setAccessTokenRefreshCallback((newAccessToken) => {
      token.value = newAccessToken
    })

    const savedToken = localStorage.getItem('auth_token')
    const savedUser = localStorage.getItem('user_data')
    const savedBusiness = localStorage.getItem('business_data')

    if (savedToken && savedUser && savedBusiness) {
      try {
        token.value = savedToken
        user.value = migrateUserFromLocalStorage(JSON.parse(savedUser))
        business.value = JSON.parse(savedBusiness)

        await getProfile()

        return true
      } catch (err) {
        console.error('Error validando token:', err)
        await logout()
        return false
      }
    }

    return false
  }

  /**
   * Entra en modo suplantación de SOLO LECTURA con un token emitido por el panel
   * admin. Limpia cualquier sesión previa, guarda el token y bootstrapea la
   * identidad + empresas del owner suplantado (reutilizando getProfile y
   * loadAndSetInitialCompany). Las escrituras quedan bloqueadas en api.js.
   */
  async function enterImpersonation({ token: impToken, ownerEmail = null, expiresAt = null }) {
    if (!impToken) throw new Error('Token de suplantación faltante')

    clearSessionStorage()
    localStorage.setItem('auth_token', impToken)
    const meta = { ownerEmail, expiresAt }
    localStorage.setItem('impersonation_meta', JSON.stringify(meta))

    token.value = impToken
    impersonation.value = meta
    isLoggingOut.value = false

    await getProfile()
    await companiesService.loadAndSetInitialCompany()

    const active = companiesService.getActiveCompany()
    if (active) {
      business.value = { ...(business.value || {}), ...active }
      _persistBusiness(business.value)
    }

    return true
  }

  /** Sale del modo suplantación: limpia sesión + flag. */
  async function exitImpersonation() {
    localStorage.removeItem('impersonation_meta')
    impersonation.value = null
    await logout()
  }

  function handleSessionExpired() {
    if (isLoggingOut.value) return
    const appStore = useAppStore()
    appStore.clearErrors()
    showSessionExpiredModal.value = true
  }

  function closeSessionExpiredModal() {
    showSessionExpiredModal.value = false
  }

  async function getProfile() {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get('/users/profile', true)
      const responseData = response.data || response

      const isPlainObject = responseData && typeof responseData === 'object' && !Array.isArray(responseData)
      if (!isPlainObject) return user.value

      const AVATAR_API_KEYS = ['avatar_url', 'avatar', 'picture', 'oauth_picture', 'google_picture', 'oauth_picture_url']
      const cleanApiData = Object.fromEntries(
        Object.entries(responseData).filter(
          ([k, v]) => !AVATAR_API_KEYS.includes(k) && v !== null && v !== undefined && v !== ''
        )
      )

      const avatarCleared =
        responseData.avatar === null ||
        responseData.avatar === '' ||
        responseData.avatar_url === null ||
        responseData.avatar_url === ''

      const updatedUser = {
        ...user.value,
        ...cleanApiData,
        name: cleanApiData.username || cleanApiData.name || user.value?.name,
        username: cleanApiData.username || user.value?.username,
        avatar: avatarCleared ? null : (avatarFromApi(responseData) ?? user.value?.avatar)
      }

      user.value = updatedUser
      localStorage.setItem('user_data', JSON.stringify(updatedUser))

      return updatedUser
    } catch (err) {
      if (isSessionExpiredError(err)) {
        error.value = null
        throw err
      }
      error.value = err.message || 'Error al obtener perfil'
      console.error('Error obteniendo perfil:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function updateProfile(profileData) {
    isLoading.value = true
    error.value = null

    try {
      const apiData = {
        username: profileData.username,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        motherLastName: profileData.motherLastName,
        birthDate: profileData.birthDate,
        gender: profileData.gender,
        email: profileData.email,
        phone1: profileData.phone1,
        phone2: profileData.phone2,
        address: profileData.address,
        neighborhood: profileData.neighborhood,
        city: profileData.city,
        state: profileData.state,
        zipCode: profileData.zipCode,
        country: profileData.country,
        avatar: profileData.avatar
      }

      Object.keys(apiData).forEach(key => {
        if (key === 'avatar' && (apiData[key] === null || apiData[key] === '')) return
        if (apiData[key] === '' || apiData[key] === null || apiData[key] === undefined) {
          delete apiData[key]
        }
      })

      const response = await api.put('/users/profile', apiData, true)
      const responseData = response.data || response
      const isPlainObject = responseData && typeof responseData === 'object' && !Array.isArray(responseData)

      const AVATAR_RESPONSE_KEYS = ['avatar_url', 'avatar', 'picture', 'oauth_picture', 'google_picture', 'oauth_picture_url']
      const cleanApiData = isPlainObject
        ? Object.fromEntries(
            Object.entries(responseData).filter(
              ([k, v]) => !AVATAR_RESPONSE_KEYS.includes(k) && v !== null && v !== undefined && v !== ''
            )
          )
        : {}

      if (isPlainObject && responseData.avatar_url && isMediaUuid(String(responseData.avatar_url))) {
        cleanApiData.avatar = String(responseData.avatar_url)
      }

      const source = isPlainObject ? cleanApiData : profileData
      const nextAvatar = resolveAvatarAfterUpdate(profileData, cleanApiData, responseData, isPlainObject, user.value)

      const updatedUser = {
        ...user.value,
        ...profileData,
        ...cleanApiData,
        name: source.username || source.name || profileData.username || profileData.name || user.value?.name,
        username: source.username || profileData.username || user.value?.username,
        email: source.email || profileData.email || user.value?.email,
        avatar: nextAvatar,
        updatedAt: cleanApiData.updatedAt || new Date().toISOString()
      }

      user.value = updatedUser
      localStorage.setItem('user_data', JSON.stringify(updatedUser))
      return updatedUser
    } catch (err) {
      error.value = err.message || 'Error al actualizar perfil'
      console.error('Error actualizando perfil:', err)

      if (user.value && (err.status === 404 || err.status === 500)) {
        console.warn('⚠️ API falló, actualizando perfil solo localmente')
        const nextAvatar = resolveAvatarAfterUpdate(profileData, {}, null, false, user.value)
        const updatedUser = {
          ...user.value,
          ...profileData,
          name: profileData.username || profileData.name || user.value?.name,
          username: profileData.username || user.value?.username,
          firstName: profileData.firstName || user.value?.firstName,
          lastName: profileData.lastName || user.value?.lastName,
          motherLastName: profileData.motherLastName || user.value?.motherLastName,
          birthDate: profileData.birthDate || user.value?.birthDate,
          gender: profileData.gender || user.value?.gender,
          email: profileData.email || user.value?.email,
          phone1: profileData.phone1 || user.value?.phone1,
          phone2: profileData.phone2 || user.value?.phone2,
          address: profileData.address || user.value?.address,
          neighborhood: profileData.neighborhood || user.value?.neighborhood,
          city: profileData.city || user.value?.city,
          state: profileData.state || user.value?.state,
          zipCode: profileData.zipCode || user.value?.zipCode,
          country: profileData.country || user.value?.country,
          avatar: nextAvatar,
          updatedAt: new Date().toISOString()
        }
        user.value = updatedUser
        localStorage.setItem('user_data', JSON.stringify(updatedUser))
        return updatedUser
      }

      throw err
    } finally {
      isLoading.value = false
    }
  }

  /** Sincroniza onboarding_completed_at tras POST /users/onboarding/complete. */
  function setOnboardingCompleted(completedAt) {
    if (!user.value) return
    user.value = { ...user.value, onboarding_completed_at: completedAt }
    localStorage.setItem('user_data', JSON.stringify(user.value))
  }

  function _persistBusiness(data) {
    if (!data) {
      localStorage.removeItem('business_data')
      return
    }
    const { logoCompany, logo, bannerCompany, logo_company, banner_company, logo_url, ...persistable } = data
    localStorage.setItem('business_data', JSON.stringify(persistable))
  }

  async function updateBusiness(businessData) {
    if (businessData === null || businessData === undefined) {
      business.value = null
      companyLogoUrl.value = null
      companyBannerUrl.value = null
      localStorage.removeItem('business_data')
      return
    }
    error.value = null
    try {
      const updatedBusiness = {
        ...(business.value || {}),
        ...businessData,
        updatedAt: new Date().toISOString()
      }
      business.value = updatedBusiness
      _persistBusiness(updatedBusiness)
      return updatedBusiness
    } catch (err) {
      error.value = err.message || 'Error al actualizar negocio'
      console.error('Error actualizando negocio:', err)
      throw err
    }
  }

  // Establece/cambia la contraseña con sesión activa (sin depender de un
  // link por email). `currentPassword` solo es obligatorio si `user.hasPassword`
  // ya era true (ver User.toJSON() en el backend) — el backend es quien decide
  // y rechaza si falta, este chequeo del front es solo para feedback rápido.
  async function updatePassword({ currentPassword, newPassword, confirmPassword }) {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.put('/users/profile/password', {
        currentPassword,
        newPassword,
        confirmPassword
      })
      if (response.success === true) {
        if (user.value) {
          user.value = { ...user.value, hasPassword: true }
          localStorage.setItem('user_data', JSON.stringify(user.value))
        }
        return { success: true, message: response.message }
      }
      error.value = response.message || 'No se pudo actualizar la contraseña'
      throw new Error(error.value)
    } catch (err) {
      if (!error.value) {
        error.value = err.message || 'Error de conexión. Intenta nuevamente.'
      }
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function forgotPassword(email) {
    isLoading.value = true
    error.value = null

    try {
      await api.post('/auth/forgot-password', { email })
      return { success: true }
    } catch (err) {
      error.value = err.message || 'Error al enviar el correo'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function verifyEmail(token) {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`, false)
      if (response.success === true) {
        if (user.value) {
          user.value = { ...user.value, email_verified: 1 }
        }
        return { success: true, message: response.message }
      }
      error.value = response.message || 'No se pudo verificar el correo'
      return { success: false }
    } catch (err) {
      error.value = err.message || 'Error al verificar el correo'
      return { success: false }
    } finally {
      isLoading.value = false
    }
  }

  async function resendVerification(email) {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post('/auth/resend-verification', { email })
      if (response.success === true) {
        return { success: true, message: response.message }
      }
      error.value = response.message || 'No se pudo reenviar el correo de verificación'
      return { success: false }
    } catch (err) {
      error.value = err.message || 'Error al reenviar el correo de verificación'
      return { success: false }
    } finally {
      isLoading.value = false
    }
  }

  async function acceptInvitation(token, password) {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post('/auth/accept-invitation', { token, password }, false)
      if (response.success === true) {
        return { success: true, message: response.message }
      }
      error.value = response.message || 'El token de invitación expiró o ya fue utilizado'
      throw new Error(error.value)
    } catch (err) {
      if (!error.value) {
        error.value = err.message || 'Error de conexión. Intenta nuevamente.'
      }
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function resetPassword(token, password) {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post('/auth/reset-password', { token, password })
      if (response.success === true) {
        return { success: true, message: response.message }
      }
      error.value = response.message || 'El enlace ha expirado o ya fue utilizado'
      throw new Error(error.value)
    } catch (err) {
      if (!error.value) {
        error.value = err.message || 'Error de conexión. Intenta nuevamente.'
      }
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function clearError() {
    error.value = null
  }

  function $reset() {
    user.value = null
    business.value = null
    isLoading.value = false
    error.value = null
    token.value = null
    isLoggingOut.value = false
    showSessionExpiredModal.value = false
    clearSessionStorage()
  }

  return {
    user,
    business,
    isLoading,
    error,
    token,
    showSessionExpiredModal,
    impersonation,
    isImpersonating,
    enterImpersonation,
    exitImpersonation,
    isAuthenticated,
    isBusinessOwner,
    isEmployee,
    userName,
    userEmail,
    avatarUrl,
    companyLogoUrl,
    companyBannerUrl,
    login,
    register,
    logout,
    setFromOAuthCallback,
    initializeAuth,
    getProfile,
    updateProfile,
    syncAvatarUrlFromUser,
    syncBusinessAssetUrlsFromBusiness,
    updateBusiness,
    setOnboardingCompleted,
    updatePassword,
    forgotPassword,
    verifyEmail,
    resendVerification,
    resetPassword,
    acceptInvitation,
    clearError,
    handleSessionExpired,
    closeSessionExpiredModal,
    $reset
  }
})